import type { BaseCanvasElement, BoundedTextElement, CanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ListElements, ElementType } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import { CanvasEx } from '@/typings/canvas';
import { v4 as uuidv4 } from 'uuid';
import { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import { ElementsPageEps } from '@/typings/customilyElements/elementsPageEps';
import { updateElementOrder } from '@/utils/elementsUtils';

interface CloneOption {
    shiftElements?: boolean;
    updateElementId?: boolean;
    updateName?: boolean;
    appendId?: boolean;
    groupId?: string | null;
    pageEpsId?: number | null;
    saveHistory?: boolean;
    useMaxZIndex?: boolean;
}

// Get the maximum pageEpsId from the canvas
const getMaxPageEpsId = (canvas: CanvasEx): number => {
    return getMaxFromElements(canvas.elementsPageEps);
};

const getMaxFromElements = (
    elements: (BaseCanvasElement | ElementsGroup | ElementsPageEps)[],
    usePosition = false
): number => {
    if (elements.length === 0) {
        console.warn('No elements provided');
        return 1; // or any other default value
    }

    return (
        Math.max(
            ...elements.map((element) => {
                if (usePosition && 'position' in element) {
                    return element.position as number;
                } else if ('id' in element) {
                    return element.id as number;
                }
                return 1;
            })
        ) + 1
    );
};

const getNewId = (canvas: CanvasEx, item: ListElements): number => {
    switch (item.type) {
        case ListElementType.CanvasElement: {
            const elements = canvas.getObjects();
            if (elements) {
                return getMaxFromElements(elements);
            }
            break;
        }
        case ListElementType.Group: {
            const groups = canvas.elementsGroups;
            if (groups) {
                return getMaxFromElements(groups, true);
            }
            break;
        }
        default:
            console.error('Invalid type');
    }

    return 1;
};

// Clone a CanvasElement and add it to the canvas
const cloneCanvasElement = async (
    item: ListElements,
    options: CloneOption,
    canvas: CanvasEx,
    duplicatedItems: ListElements[] = []
): Promise<CanvasElement> => {
    const newElement = (await canvas?.cloneObject(item.element, true)) as CanvasElement;

    const shiftValue = options?.shiftElements ? 50 : 0;
    const idValue = options?.updateElementId ? getNewId(canvas, item) : newElement.id;

    // Assign new UUID and position
    newElement.uuid = uuidv4();

    if (newElement.type === 'text-transform') {
        newElement.left = item.element.left + shiftValue;
        newElement.top = item.element.top + shiftValue;
        newElement.fire?.('moving', newElement);
        newElement.setCoords();
        newElement.setEditMode?.(undefined);
    } else {
        newElement.left =
            (item.element.calcTransformMatrix ? item.element.calcTransformMatrix()[4] : newElement.left) + shiftValue;
        newElement.top =
            (item.element.calcTransformMatrix ? item.element.calcTransformMatrix()[5] : newElement.top) + shiftValue;
        newElement.setCoords();
    }

    newElement.addedOnEdit = true;
    newElement.id = idValue;
    newElement.name = item.element.name;
    if (options?.updateName) {
        newElement.name = `${newElement.name} (Cloned)`;
    }
    if (options?.appendId) {
        newElement.name = `${newElement.name} ${idValue}`;
    }
    newElement.zIndex = options.useMaxZIndex ? canvas.getHighestZIndex() + 1 : newElement.zIndex + 1;

    // Assign optional groupId and pageEpsId
    if (options.groupId) newElement.groupId = options.groupId;
    if (options.pageEpsId) newElement.pageEpsId = options.pageEpsId;

    canvas.addWithoutHistory(newElement);

    if(newElement.type === 'bounded-text') {
        const text = newElement as BoundedTextElement;
        if (text && text.sourceElement) {
            const { sourceId, sourceType, valueFormat } = text.sourceElement
            canvas.addLinkElements(sourceId, text, sourceType, valueFormat);
        }
    }

    duplicatedItems.push({
        type: ListElementType.CanvasElement,
        element: newElement,
        zIndex: newElement.zIndex
    });

    canvas.requestRenderAll();

    return newElement;
};

// Clone a Group and its children, then add it to the canvas
const cloneGroup = async (
    item: ListElements,
    options: CloneOption,
    canvas: CanvasEx,
    duplicatedItems: ListElements[]
) => {
    const newGroup = canvas?.cloneGroup(item.element);

    const idValue = options?.updateElementId ? getNewId(canvas, item) : newGroup.position;

    // Assign new UUID and name
    newGroup.position = idValue;
    newGroup.id = uuidv4();
    newGroup.uuid = uuidv4();
    newGroup.name = item.element.name || 'Layout';
    if (options?.updateName) {
        newGroup.name = `${newGroup.name} (Cloned)`;
    }
    if (options?.appendId) {
        newGroup.name = `${newGroup.name} ${idValue}`;
    }
    newGroup.addedOnEdit = true;
    newGroup.zIndex = options.useMaxZIndex ? canvas.getHighestZIndex() + 1 : newGroup.zIndex + 1;

    // Assign optional parentId and pageEpsId
    if (options.groupId) newGroup.parentId = options.groupId;
    if (options.pageEpsId) newGroup.pageEpsId = options.pageEpsId;

    canvas.addGroup(newGroup);

    duplicatedItems.push({
        type: 'Group',
        element: newGroup,
        children: [],
        zIndex: newGroup.zIndex
    } as ListElements);

    // Clone children recursively
    const children = item.children || [];

    await duplicateItemsDeep({
        items: children,
        options: {
            shiftElements: options?.shiftElements,
            groupId: newGroup.id,
            pageEpsId: newGroup.pageEpsId
        },
        canvas,
        duplicatedItems
    });

    return newGroup;
};

// Clone a PageEps and its children, then add it to the canvas
const clonePageEps = async (
    item: ListElements,
    options: CloneOption,
    canvas: CanvasEx,
    duplicatedItems: ListElements[]
) => {
    const newPageEps = canvas?.clonePageEps(item.element);
    const idValue = getMaxPageEpsId(canvas) + 1;

    // Assign new ID, UUID, and name
    newPageEps.id = idValue;
    newPageEps.uuid = uuidv4();
    newPageEps.name = item.element.name || `File ${idValue}`;
    if (options?.updateName) {
        newPageEps.name = `${newPageEps.name} (Cloned)`;
    }
    if (options?.appendId) {
        newPageEps.name = `${newPageEps.name} ${idValue}`;
    }
    newPageEps.addedOnEdit = true;
    newPageEps.zIndex = options.useMaxZIndex ? canvas.getHighestZIndex() + 1 : newPageEps.zIndex + 1;

    canvas.addPageEps(newPageEps);

    duplicatedItems.push({
        type: 'PageEps',
        element: newPageEps,
        children: [],
        zIndex: newPageEps.zIndex
    } as ListElements);

    // Clone children recursively
    const children = item.children || [];

    await duplicateItemsDeep({
        items: children,
        options: {
            shiftElements: options?.shiftElements,
            pageEpsId: newPageEps.id
        },
        canvas,
        duplicatedItems
    });

    return newPageEps;
};

// Main function to duplicate elements deeply based on their type
export const duplicateItemsDeep = async ({
    items,
    options,
    canvas,
    duplicatedItems = []
}: {
    items: ListElements[];
    options: CloneOption;
    canvas: CanvasEx;
    duplicatedItems: ListElements[];
}): Promise<ElementType[]> => {
    if (!items || !items.length) {
        console.warn('No items provided');
        return [];
    }

    const newElements: ElementType[] = [];

    const invertedOrderItems = items.reverse();
    const enableUseMaxZIndex = invertedOrderItems.length > 1;

    if (enableUseMaxZIndex) {
        options.useMaxZIndex = true;
    }

    for (const item of invertedOrderItems) {
        switch (item.type) {
            case 'CanvasElement':
                newElements.push(await cloneCanvasElement(item, options, canvas, duplicatedItems));
                break;
            case 'Group':
                newElements.push(await cloneGroup(item, options, canvas, duplicatedItems));
                break;
            case 'PageEps':
                newElements.push(await clonePageEps(item, options, canvas, duplicatedItems));
                break;
        }
    }

    updateElementOrder(newElements, canvas, true);

    if (options.saveHistory) {
        canvas.undoManager?.add(
            (duplicatedItems: ListElements[]) => {
                duplicatedItems.forEach((item) => {
                    const element = item.element;
                    if (item.type === 'CanvasElement') {
                        canvas.add(element);
                        canvas.moveTo(element, element.zIndex);
                    } else if (item.type === 'Group') {
                        canvas.addGroup(element);
                    } else if (item.type === 'PageEps') {
                        canvas.addPageEps(element);
                    }
                });
            },
            [duplicatedItems],
            (duplicatedItems: ListElements[]) => {
                canvas.discardActiveObject();
                duplicatedItems.forEach((item) => {
                    const element = item.element;
                    if (item.type === 'CanvasElement') {
                        canvas.remove(element);
                    } else if (item.type === 'Group') {
                        canvas.removeGroup(element);
                    } else if (item.type === 'PageEps') {
                        canvas.removePageEps.apply(canvas, [element, false]);
                    }
                });
            },
            [duplicatedItems]
        );
    }

    canvas.requestRenderAll();

    return newElements;
};
